import {ChangeEvent, useContext, useEffect} from 'react';
import {PropsOf} from '@emotion/react';
import {Value} from 'patient-ping-remedy/packages/dropdown-list/lib/DropdownList';
import DropdownList from 'patient-ping-remedy/packages/dropdown-list';
import {Client} from '@/api/dto/dto';
import {Patient} from '@/api/dto/client-profile';
import {GenericObject} from '@/hooks/useForm';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useRosterStore} from '@/store/roster_store';
import {useClientStore} from '@/store/client_store';
import DisplayHelpers from '@/helpers/display_helpers';

const formatClientOption = (client: Client | Patient): Value => ({
  label: `${DisplayHelpers.formatName(client.firstName, client.middleName, client.lastName)}
    - DOB ${DisplayHelpers.formatDateOfBirthWithAge(client.dateOfBirth)}`,
  value: client.patientId,
});

type Props = PropsOf<typeof DropdownList> & {
  form: GenericObject;
  handleChange(e: ChangeEvent | null, name: string, value: any): boolean;
  rosterId?: number;
}

export default function ClientDropdownList({ form, handleChange, rosterId, ...props }: Props) {
  const { carecoApi } = useContext(CarecoApiContext);
  const { currentRoster } = useRosterStore();
  const { currentClient } = useClientStore();

  // Callers on the cross-roster Tasks page (where currentRoster is null) can pass an explicit roster
  // to scope the client search -- see CARECO-5939. Falls back to the facility picker's currentRoster
  // for all existing callers.
  const searchRosterId = rosterId ?? currentRoster?.id ?? 0;

  const handleClientSearch = async (searchTerm: string) => {
    const clients = await carecoApi?.getClients(searchRosterId, 1, searchTerm);
    return clients?.data.clients.map((client) => formatClientOption(client)) ?? [];
  };

  useEffect(() => {
    if (currentClient && !form.client?.value) {
      handleChange(null, 'client', formatClientOption(currentClient.patient));
    }
  }, []);

  return (
    <DropdownList
      label="Client"
      items={currentClient ? [formatClientOption(currentClient.patient)] : []}
      value={form.client?.value}
      handleChange={(value: Value | null) => handleChange(null, 'client', value)}
      handleSearch={handleClientSearch}
      placeholder="Search by client name"
      error={!form.client?.valid?.valid && form.client?.valid?.message}
      errorMessage={form.client?.valid?.message}
      {...props}
    />
  );
}
